/**
 * Create / edit one tier-name entry (canonical name, synonyms, rank,
 * description). Generic grade names only — brand tiers live on calibres. Validates against the whole list as it would
 * stand after the save, so a duplicate name is refused here, before the write —
 * the transactional writer re-checks against the stored list anyway.
 */

import { useMemo, useState } from 'react';
import type { TierVocabularyEntry } from '@watchkeeper/shared-types';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { TagInput } from '../CuratedWatches/TagInput';
import {
  newTierEntryId,
  normaliseTierEntry,
  upsertTierEntry,
  validateTierVocabulary,
} from '../../utils/tierVocabulary';

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

interface Props {
  /** null = new entry. */
  initial: TierVocabularyEntry | null;
  /** The owner's current list — the candidate is validated as part of it. */
  list: TierVocabularyEntry[];
  /** Another list this one must not shadow (see validateTierVocabulary). */
  reserved?: TierVocabularyEntry[];
  /** Resolved brand names for ids already on the list. */
  brandNames: Record<string, string>;
  ownerName: string;
  onClose: () => void;
  /** Throws to keep the modal open (the write failed). */
  onSave: (entry: TierVocabularyEntry) => Promise<void>;
}

export function TierVocabEntryModal({ initial, list, reserved, brandNames, ownerName, onClose, onSave }: Props) {
  const [canonicalName, setCanonicalName] = useState(initial?.canonicalName ?? '');
  const [akas, setAkas] = useState<string[]>(initial?.akas ?? []);
  const [rank, setRank] = useState<string>(initial?.rank !== undefined ? String(initial.rank) : '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [brandId, setBrandId] = useState(initial?.brandId ?? '');
  const [brandLabel] = useState(initial?.brandId ? (brandNames[initial.brandId] ?? initial.brandId) : '');
  const [saving, setSaving] = useState(false);
  const [id] = useState(() => initial?.id ?? newTierEntryId());

  const candidate = useMemo(
    () =>
      normaliseTierEntry({
        id,
        canonicalName,
        akas,
        ...(rank.trim() !== '' && Number.isFinite(Number(rank)) ? { rank: Number(rank) } : {}),
        description,
        brandId,
      }),
    [id, canonicalName, akas, rank, description, brandId],
  );
  const issues = useMemo(
    () => validateTierVocabulary(upsertTierEntry(list, candidate), reserved),
    [list, candidate, reserved],
  );
  const rankInvalid = rank.trim() !== '' && (!Number.isInteger(Number(rank)) || Number(rank) < 0);
  const blocked = !candidate.canonicalName || issues.errors.length > 0 || rankInvalid;

  const save = async () => {
    if (blocked) return;
    setSaving(true);
    try {
      await onSave(candidate);
    } catch {
      // The caller toasts; stay open so nothing typed is lost.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={initial ? `Edit tier name — ${ownerName}` : `Add tier name — ${ownerName}`} size="md">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Canonical name<span className="text-red-500"> *</span>
          </label>
          <input
            type="text"
            value={canonicalName}
            onChange={(e) => setCanonicalName(e.target.value)}
            placeholder="The manufacturer's own spelling, e.g. Elaboré"
            className={inputClass}
            autoFocus
          />
        </div>
        <TagInput label="Other spellings (AKAs)" values={akas} onChange={setAkas} placeholder="e.g. Special, Spécial — Enter to add" />
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Rank</label>
            <input
              type="number"
              min={0}
              step={1}
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              placeholder="0 = base"
              className={inputClass}
            />
            {rankInvalid && <p className="text-xs text-red-600 mt-1">A whole number, 0 or more.</p>}
          </div>
          {/* No brand picker: these lists hold generic grade names only. A
              legacy brand-scoped entry says so, and validation refuses it. */}
          {brandId && (
            <div className="col-span-2 text-xs text-amber-700">
              This entry is scoped to {brandLabel || brandId}. Brand tiers now live on the calibre — remove the brand
              or delete this entry.{' '}
              <button type="button" className="underline" onClick={() => setBrandId('')}>
                Remove the brand
              </button>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="One sentence: what the grade changes"
            className={inputClass}
          />
        </div>

        {issues.errors.length > 0 && (
          <ul className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 space-y-0.5">
            {issues.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {issues.warnings.length > 0 && (
          <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5">
            {issues.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={blocked} loading={saving}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
